import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'
import { Badge } from "./ui/badge"
import { Button } from "./ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card"
import { Input } from "./ui/input"
import { Label } from "./ui/label"
import { Checkbox } from "./ui/checkbox"

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarNav,
  SidebarNavItem,
} from "./ui/sidebar"
import {
  Calendar,
  Cloud,
  Droplets,
  Home,
  Leaf,
  Settings,
  Sun,
  Thermometer,
  User,
  CheckCircle2,
  AlertTriangle,
  Sprout,
  TreePine,
  LogOut,
  Menu,
  X,
  Search,
  Filter,
  Star,
  Plus,
  ChevronDown,
  Heart,
} from "lucide-react"
import SharedHeader from './SharedHeader'

// Sample plant data
const samplePlants = [
  {
    id: 1,
    name: "Rožė",
    scientificName: "Rosa",
    category: "Gėlės",
    difficulty: "Vidutinis",
    sunlight: "full_sun",
    watering: "weekly",
    description: "Populiari dekoratyvinė gėlė su gražiais žiedais",
    image: "https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=400&h=300&fit=crop"
  },
  {
    id: 2,
    name: "Pomidoras",
    scientificName: "Solanum lycopersicum",
    category: "Daržovės",
    difficulty: "Lengvas",
    sunlight: "full_sun",
    watering: "daily",
    description: "Populiari daržovė, auginama soduose",
    image: "https://images.unsplash.com/photo-1592924357228-91a4daadcfea?w=400&h=300&fit=crop"
  },
  {
    id: 3,
    name: "Ožiarūtis",
    scientificName: "Thymus",
    category: "Žolynai",
    difficulty: "Lengvas",
    sunlight: "full_sun",
    watering: "weekly",
    description: "Aromatinis žolynas virtuvėms",
    image: "https://images.unsplash.com/photo-1551522435-a13afa10f103?w=400&h=300&fit=crop"
  },
  {
    id: 4,
    name: "Ąžuolas",
    scientificName: "Quercus",
    category: "Medžiai",
    difficulty: "Sunkus",
    sunlight: "full_sun",
    watering: "monthly",
    description: "Didelis, ilgaamžis medis",
    image: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=400&h=300&fit=crop"
  },
  {
    id: 5,
    name: "Agrastas",
    scientificName: "Ribes uva-crispa",
    category: "Krūmai",
    difficulty: "Vidutinis",
    sunlight: "partial_shade",
    watering: "weekly",
    description: "Vaisinis krūmas su skaniais uogais",
    image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=300&fit=crop"
  },
  {
    id: 6,
    name: "Tulipė",
    scientificName: "Tulipa",
    category: "Gėlės",
    difficulty: "Lengvas",
    sunlight: "full_sun",
    watering: "weekly",
    description: "Pavasarinė gėlė su gražiais žiedais",
    image: "https://images.unsplash.com/photo-1520607162513-77705c0f0d4a?w=400&h=300&fit=crop"
  },
  {
    id: 7,
    name: "Agurkas",
    scientificName: "Cucumis sativus",
    category: "Daržovės",
    difficulty: "Vidutinis",
    sunlight: "full_sun",
    watering: "daily",
    description: "Šviežia daržovė salotoms",
    image: "https://images.unsplash.com/photo-1449300079323-02e209d9d3a6?w=400&h=300&fit=crop"
  },
  {
    id: 8,
    name: "Mėta",
    scientificName: "Mentha",
    category: "Žolynai",
    difficulty: "Lengvas",
    sunlight: "partial_shade",
    watering: "weekly",
    description: "Aromatinis žolynas arbatai",
    image: "https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=400&h=300&fit=crop"
  },
  {
    id: 9,
    name: "Klevas",
    scientificName: "Acer",
    category: "Medžiai",
    difficulty: "Vidutinis",
    sunlight: "partial_shade",
    watering: "monthly",
    description: "Dekoratyvinis medis parkuose",
    image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=300&fit=crop"
  },
  {
    id: 10,
    name: "Avietė",
    scientificName: "Rubus idaeus",
    category: "Krūmai",
    difficulty: "Vidutinis",
    sunlight: "partial_shade",
    watering: "weekly",
    description: "Vaisinis krūmas su skaniais uogais",
    image: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=400&h=300&fit=crop"
  },
  {
    id: 11,
    name: "Lelija",
    scientificName: "Lilium",
    category: "Gėlės",
    difficulty: "Vidutinis",
    sunlight: "partial_shade",
    watering: "weekly",
    description: "Graži gėlė su dideliais žiedais",
    image: "https://images.unsplash.com/photo-1593691509543-c55fb32e5cee?w=400&h=300&fit=crop"
  },
  {
    id: 12,
    name: "Bulvė",
    scientificName: "Solanum tuberosum",
    category: "Daržovės",
    difficulty: "Lengvas",
    sunlight: "full_sun",
    watering: "weekly",
    description: "Pagrindinė daržovė virtuvėms",
    image: "https://images.unsplash.com/photo-1518977676601-b53f82aba655?w=400&h=300&fit=crop"
  }
]

export default function PlantCatalog() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout, isAuthenticated } = useAuth()
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)
  const [filterSidebarOpen, setFilterSidebarOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategories, setSelectedCategories] = useState<string[]>([])
  const [selectedDifficulties, setSelectedDifficulties] = useState<string[]>([])
  const [selectedSunlight, setSelectedSunlight] = useState<string[]>([])
  const [selectedWatering, setSelectedWatering] = useState<string[]>([])


  // Show loading state while authentication is being checked
  if (!isAuthenticated) {
    return (
      <div className="flex h-screen items-center justify-center bg-gradient-to-br from-emerald-50 via-green-50 to-teal-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-emerald-700">Kraunama...</p>
        </div>
      </div>
    )
  }

  const navigationItems = [
    {
      title: "Pagrindinis",
      description: "Pagrindinis skydelis",
      url: '/dashboard',
      icon: Home
    },
    {
      title: "Mano Sodas",
      description: "Jūsų augalų sąrašas",
      url: '/my-plants',
      icon: Heart
    },
    {
      title: "Augalų Katalogas",
      description: "Augalų duomenų bazė",
      url: '/plants',
      icon: Leaf
    },
    {
      title: "Užduotys",
      description: "Priežiūros užduotys",
      url: '/tasks',
      icon: CheckCircle2
    },
    {
      title: "Kalendorius",
      description: "Priežiūros kalendorius",
      url: '/calendar',
      icon: Calendar
    },
    {
      title: "Orai",
      description: "Oro prognozės",
      url: '/weather',
      icon: Cloud
    }
  ]

  const categories = ["Gėlės", "Daržovės", "Žolynai", "Medžiai", "Krūmai"]
  const difficulties = ["Lengvas", "Vidutinis", "Sunkus"]
  const sunlightOptions = ["full_sun", "partial_shade", "full_shade"]
  const wateringOptions = ["daily", "weekly", "monthly"]



  const handleNavigation = (url: string) => {
    navigate(url)
    setMobileSidebarOpen(false)
  }



  const isActiveRoute = (url: string) => {
    return location.pathname === url
  }

  const getDifficultyStars = (difficulty: string) => {
    const starCount = difficulty === "Lengvas" ? 1 : difficulty === "Vidutinis" ? 2 : 3
    return Array.from({ length: 3 }, (_, i) => (
      <Star
        key={i}
        className={`h-3 w-3 ${i < starCount ? 'text-amber-500 fill-current' : 'text-gray-300'}`}
      />
    ))
  }

  const getSunlightIcon = (sunlight: string) => {
    switch (sunlight) {
      case 'full_sun':
        return <Sun className="h-4 w-4 text-amber-500" />
      case 'partial_shade':
        return <Cloud className="h-4 w-4 text-gray-500" />
      case 'full_shade':
        return <Cloud className="h-4 w-4 text-gray-700" />
      default:
        return <Sun className="h-4 w-4 text-gray-400" />
    }
  }

  const getWateringIcon = (watering: string) => {
    return <Droplets className="h-4 w-4 text-blue-500" />
  }

  const getSunlightText = (sunlight: string) => {
    switch (sunlight) {
      case 'full_sun':
        return 'Pilna saulė'
      case 'partial_shade':
        return 'Pusiau šešėlis'
      case 'full_shade':
        return 'Pilnas šešėlis'
      default:
        return sunlight
    }
  }

  const getWateringText = (watering: string) => {
    switch (watering) {
      case 'daily':
        return 'Kasdien'
      case 'weekly':
        return 'Kas savaitę'
      case 'monthly':
        return 'Kas mėnesį'
      default:
        return watering
    }
  }

  // Filter plants based on search and filters
  const filteredPlants = samplePlants.filter(plant => {
    const matchesSearch = plant.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         plant.scientificName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         plant.description.toLowerCase().includes(searchQuery.toLowerCase())
    
    const matchesCategory = selectedCategories.length === 0 || selectedCategories.includes(plant.category)
    const matchesDifficulty = selectedDifficulties.length === 0 || selectedDifficulties.includes(plant.difficulty)
    const matchesSunlight = selectedSunlight.length === 0 || selectedSunlight.includes(plant.sunlight)
    const matchesWatering = selectedWatering.length === 0 || selectedWatering.includes(plant.watering)

    return matchesSearch && matchesCategory && matchesDifficulty && matchesSunlight && matchesWatering
  })

  const handleAddToGarden = (plantId: number) => {
    // TODO: Implement add to garden functionality
    console.log(`Adding plant ${plantId} to garden`)
  }

  // Plant Placeholder Component
  const PlantPlaceholder = () => (
    <div className="w-full h-full bg-emerald-50 flex items-center justify-center rounded-lg">
      <Leaf className="w-12 h-12 text-emerald-400" />
    </div>
  )

  return (
    <div className="flex h-screen bg-gradient-to-br from-emerald-50 via-green-50 to-teal-50 plant-catalog-page">
      {/* Desktop Sidebar */}
      <Sidebar className={`hidden lg:flex w-64 flex-col bg-white/80 backdrop-blur-sm border-r border-emerald-100 shadow-2xl transition-all duration-300 ${sidebarOpen ? 'w-64' : 'w-16'}`}>
        {/* Sidebar Header */}
        <SidebarHeader className="bg-gradient-to-br from-emerald-50 to-green-50 border-b border-emerald-100 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-600 text-white shadow-md">
              <Leaf className="h-5 w-5" />
            </div>
            {sidebarOpen && (
              <div className="flex flex-col">
                <span className="text-lg font-bold text-emerald-800">{t('branding.appName')}</span>
                <span className="text-xs text-emerald-600">{t('dashboard.sidebar.subtitle')}</span>
              </div>
            )}
          </div>
        </SidebarHeader>
        
        {/* Navigation Content */}
        <SidebarContent className="flex-1 bg-white/80 backdrop-blur-sm px-3 py-4">
          <SidebarNav className="space-y-1">
            {navigationItems.map((item) => {
              const isActive = isActiveRoute(item.url)
              return (
                <SidebarNavItem
                  key={item.title}
                  active={isActive}
                  className={`
                    group relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200 cursor-pointer
                    ${isActive 
                      ? 'bg-emerald-50 text-emerald-700 border-l-4 border-emerald-600 shadow-sm' 
                      : 'text-gray-700 hover:bg-emerald-50 hover:text-emerald-700'
                    }
                  `}
                  onClick={() => handleNavigation(item.url)}
                >
                  <item.icon className={`h-5 w-5 transition-colors ${isActive ? 'text-emerald-600' : 'text-gray-500 group-hover:text-emerald-600'}`} />
                  {sidebarOpen && (
                    <div className="flex flex-col">
                      <span>{item.title}</span>
                      <span className="text-xs text-gray-500 font-normal">{item.description}</span>
                    </div>
                  )}
                </SidebarNavItem>
              )
            })}
          </SidebarNav>
        </SidebarContent>
        
        {/* Sidebar Footer */}
        <SidebarFooter className="bg-gradient-to-t from-emerald-50 to-transparent border-t border-emerald-100 px-3 py-4">
          <SidebarNav>
            <SidebarNavItem 
              className="group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 transition-all duration-200 cursor-pointer"
            >
              <Settings className="h-5 w-5 text-gray-500 group-hover:text-emerald-600 transition-colors" />
              {sidebarOpen && (
                <div className="flex flex-col">
                  <span>{t('dashboard.navigation.settings')}</span>
                  <span className="text-xs text-gray-500 font-normal">Aplikacijos nustatymai</span>
                </div>
              )}
            </SidebarNavItem>
          </SidebarNav>
        </SidebarFooter>
      </Sidebar>

      {/* Mobile Sidebar Overlay */}
      {mobileSidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-black/50" onClick={() => setMobileSidebarOpen(false)} />
          <div className="fixed left-0 top-0 h-full w-64 bg-white/80 backdrop-blur-sm shadow-2xl">
            <div className="flex h-full flex-col">
              {/* Mobile Header */}
              <div className="bg-gradient-to-br from-emerald-50 to-green-50 border-b border-emerald-100 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-600 text-white shadow-md">
                      <Leaf className="h-5 w-5" />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-lg font-bold text-emerald-800">{t('branding.appName')}</span>
                      <span className="text-xs text-emerald-600">{t('dashboard.sidebar.subtitle')}</span>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-emerald-700 hover:bg-emerald-100"
                    onClick={() => setMobileSidebarOpen(false)}
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </div>
              </div>

              {/* Mobile Navigation */}
              <div className="flex-1 px-3 py-4">
                <SidebarNav className="space-y-1">
                  {navigationItems.map((item) => {
                    const isActive = isActiveRoute(item.url)
                    return (
                      <SidebarNavItem
                        key={item.title}
                        active={isActive}
                        className={`
                          group relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200 cursor-pointer
                          ${isActive 
                            ? 'bg-emerald-50 text-emerald-700 border-l-4 border-emerald-600 shadow-sm' 
                            : 'text-gray-700 hover:bg-emerald-50 hover:text-emerald-700'
                          }
                        `}
                        onClick={() => handleNavigation(item.url)}
                      >
                        <item.icon className={`h-5 w-5 transition-colors ${isActive ? 'text-emerald-600' : 'text-gray-500 group-hover:text-emerald-600'}`} />
                        <div className="flex flex-col">
                          <span>{item.title}</span>
                          <span className="text-xs text-gray-500 font-normal">{item.description}</span>
                        </div>
                      </SidebarNavItem>
                    )
                  })}
                </SidebarNav>
              </div>

              {/* Mobile Footer */}
              <div className="bg-gradient-to-t from-emerald-50 to-transparent border-t border-emerald-100 px-3 py-4">
                <SidebarNav>
                  <SidebarNavItem 
                    href="#" 
                    className="group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 transition-all duration-200"
                  >
                    <Settings className="h-5 w-5 text-gray-500 group-hover:text-emerald-600 transition-colors" />
                    <div className="flex flex-col">
                      <span>{t('dashboard.navigation.settings')}</span>
                      <span className="text-xs text-gray-500 font-normal">Aplikacijos nustatymai</span>
                    </div>
                  </SidebarNavItem>
                </SidebarNav>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <SharedHeader 
          title="Augalų katalogas"
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          mobileSidebarOpen={mobileSidebarOpen}
          setMobileSidebarOpen={setMobileSidebarOpen}
        />

        {/* Main Content */}
        <main className="flex-1 flex overflow-hidden">
          {/* Filter Sidebar */}
          <div className={`w-80 bg-white/80 backdrop-blur-sm border-r border-emerald-100 shadow-lg transition-all duration-300 ${filterSidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 lg:static fixed inset-y-0 left-0 z-40`}>
            <div className="p-6 space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-emerald-800">Filtrai</h2>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-emerald-700 hover:bg-emerald-50 lg:hidden"
                  onClick={() => setFilterSidebarOpen(false)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              {/* Search */}
              <div className="space-y-2">
                <Label htmlFor="search" className="text-sm font-medium text-gray-700">
                  Ieškoti
                </Label>
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="search"
                    placeholder="Ieškoti augalų..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              {/* Categories */}
              <div className="space-y-3">
                <Label className="text-sm font-medium text-gray-700">Kategorijos</Label>
                {categories.map((category) => (
                  <div key={category} className="flex items-center space-x-2">
                    <Checkbox
                      id={category}
                      checked={selectedCategories.includes(category)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedCategories([...selectedCategories, category])
                        } else {
                          setSelectedCategories(selectedCategories.filter(c => c !== category))
                        }
                      }}
                    />
                    <Label htmlFor={category} className="text-sm text-gray-600">
                      {category}
                    </Label>
                  </div>
                ))}
              </div>

              {/* Difficulty */}
              <div className="space-y-3">
                <Label className="text-sm font-medium text-gray-700">Sunkumas</Label>
                {difficulties.map((difficulty) => (
                  <div key={difficulty} className="flex items-center space-x-2">
                    <Checkbox
                      id={difficulty}
                      checked={selectedDifficulties.includes(difficulty)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedDifficulties([...selectedDifficulties, difficulty])
                        } else {
                          setSelectedDifficulties(selectedDifficulties.filter(d => d !== difficulty))
                        }
                      }}
                    />
                    <Label htmlFor={difficulty} className="text-sm text-gray-600">
                      {difficulty}
                    </Label>
                  </div>
                ))}
              </div>

              {/* Sunlight */}
              <div className="space-y-3">
                <Label className="text-sm font-medium text-gray-700">Saulės šviesa</Label>
                {sunlightOptions.map((sunlight) => (
                  <div key={sunlight} className="flex items-center space-x-2">
                    <Checkbox
                      id={sunlight}
                      checked={selectedSunlight.includes(sunlight)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedSunlight([...selectedSunlight, sunlight])
                        } else {
                          setSelectedSunlight(selectedSunlight.filter(s => s !== sunlight))
                        }
                      }}
                    />
                    <Label htmlFor={sunlight} className="text-sm text-gray-600">
                      {getSunlightText(sunlight)}
                    </Label>
                  </div>
                ))}
              </div>

              {/* Watering */}
              <div className="space-y-3">
                <Label className="text-sm font-medium text-gray-700">Laistymas</Label>
                {wateringOptions.map((watering) => (
                  <div key={watering} className="flex items-center space-x-2">
                    <Checkbox
                      id={watering}
                      checked={selectedWatering.includes(watering)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedWatering([...selectedWatering, watering])
                        } else {
                          setSelectedWatering(selectedWatering.filter(w => w !== watering))
                        }
                      }}
                    />
                    <Label htmlFor={watering} className="text-sm text-gray-600">
                      {getWateringText(watering)}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Plant Grid */}
          <div className="flex-1 p-6 overflow-auto">
            {/* Mobile Filter Toggle */}
            <div className="lg:hidden mb-4">
              <Button
                variant="outline"
                className="w-full border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                onClick={() => setFilterSidebarOpen(true)}
              >
                <Filter className="h-4 w-4 mr-2" />
                Filtrai
              </Button>
            </div>

            {/* Results Count */}
            <div className="mb-6">
              <p className="text-sm text-gray-600">
                Rasta {filteredPlants.length} augalų
              </p>
            </div>

            {/* Plant Grid */}
            <div className="grid gap-6 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {filteredPlants.map((plant) => (
                <Card key={plant.id} className="border-0 shadow-2xl hover:shadow-3xl transition-shadow duration-200 bg-white/80 backdrop-blur-sm overflow-hidden">
                  <div className="aspect-square bg-emerald-50 flex items-center justify-center relative overflow-hidden rounded-t-lg">
                    <img
                      src={plant.image}
                      alt={`${plant.name} (${plant.scientificName})`}
                      className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none'
                        e.currentTarget.nextElementSibling?.classList.remove('hidden')
                      }}
                    />
                    <div className="hidden absolute inset-0">
                      <PlantPlaceholder />
                    </div>
                  </div>
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="text-lg font-semibold text-gray-900 mb-1">
                          {plant.name}
                        </CardTitle>
                        <CardDescription className="text-sm text-gray-500 italic">
                          {plant.scientificName}
                        </CardDescription>
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {plant.category}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-sm text-gray-600 line-clamp-2">
                      {plant.description}
                    </p>
                    
                    <div className="space-y-3">
                      {/* Difficulty */}
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Sunkumas:</span>
                        <div className="flex gap-1">
                          {getDifficultyStars(plant.difficulty)}
                        </div>
                      </div>

                      {/* Sunlight & Watering */}
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <div className="flex items-center gap-1">
                          {getSunlightIcon(plant.sunlight)}
                          <span>{getSunlightText(plant.sunlight)}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          {getWateringIcon(plant.watering)}
                          <span>{getWateringText(plant.watering)}</span>
                        </div>
                      </div>
                    </div>

                    <Button
                      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={() => handleAddToGarden(plant.id)}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Pridėti į sodą
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* No Results */}
            {filteredPlants.length === 0 && (
              <div className="text-center py-12">
                <Leaf className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Augalų nerasta</h3>
                <p className="text-gray-500">Pabandykite pakeisti paieškos kriterijus</p>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  )
} 